import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Stack,
  Alert,
  Button,
} from '@mui/material';
// import { ContentCopy } from '@mui/icons-material';

interface PIJobReportHelperProps {
  onExtract?: (data: any) => void;
}

const PIJobReportHelper: React.FC<PIJobReportHelperProps> = ({ onExtract }) => {
  const [pdfText, setPdfText] = useState('');
  const [extractedData, setExtractedData] = useState<any>(null);

  const handleTextAnalysis = () => {
    const lines = pdfText.split('\n').map(line => line.trim()).filter(line => line);
    
    // Extract job title and owner from first lines
    let jobTitle = '';
    let department = '';
    const jobLine = lines.find(line => line.startsWith('Job:'));
    if (jobLine) {
      const match = jobLine.match(/Job:(.+?)Owned by:/);
      if (match) {
        jobTitle = match[1].trim();
        // Try to extract department from title
        if (jobTitle.includes(' - ')) {
          const parts = jobTitle.split(' - ');
          jobTitle = parts[0].trim();
          department = parts[1].trim();
        }
      }
    }
    
    // Extract job summary
    const summaryIndex = lines.findIndex(line => line.toLowerCase().includes('job summary'));
    let jobSummary = '';
    if (summaryIndex !== -1) {
      // Get text after "Job summary" until next section
      for (let i = summaryIndex + 1; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes('job characteristics') || 
            lines[i].toLowerCase().includes('behavioral target')) {
          break;
        }
        jobSummary += lines[i] + ' ';
      }
    }

    // Extract common reference profiles
    const profilesIndex = lines.findIndex(line => line.toLowerCase().includes('common reference profiles'));
    const profiles = [];
    if (profilesIndex !== -1) {
      for (let i = profilesIndex + 1; i < Math.min(profilesIndex + 20, lines.length); i++) {
        if (lines[i].match(/^(Artisan|Operator|Guardian|Captain|Venturer|Analyzer|Controller|Specialist|Scholar|Individualist|Strategist|Persuader|Altruist|Maverick|Adapter|Promoter|Collaborator)/)) {
          profiles.push(lines[i]);
        }
      }
    }

    // Look for "They will be:" section
    const behaviorIndex = lines.findIndex(line => line === 'They will be:');
    const behaviors = [];
    if (behaviorIndex !== -1) {
      for (let i = behaviorIndex + 1; i < lines.length; i++) {
        if (lines[i] === 'They will need:' || lines[i].includes('Preferred job characteristics')) {
          break;
        }
        if (lines[i]) behaviors.push(lines[i]);
      }
    }

    // Extract job characteristics section
    const charIndex = lines.findIndex(line => line.toLowerCase().includes('job characteristics'));
    let jobCharacteristics = '';
    if (charIndex !== -1) {
      for (let i = charIndex + 1; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes('behavioral target') || 
            lines[i].includes('Your ideal candidate')) {
          break;
        }
        jobCharacteristics += lines[i] + ' ';
      }
    }

    const extracted = {
      jobTitle: jobTitle,
      department: department,
      jobSummary: jobSummary.trim(),
      jobCharacteristics: jobCharacteristics.trim(),
      commonProfiles: profiles,
      behaviors: behaviors,
      // Default PI ranges based on PDF description
      suggestedRanges: {
        // For Electronics Engineering Technician from PDF:
        // Low A (Collaborative) ~ -1σ, Low B (Reserved) ~ -1σ, High C (Steady) ~ +1σ, High D (Precise) ~ +1σ
        // Each range is 1σ wide (±0.5σ from center)
        ideal_factor_a_min: 25,  // -1.5σ
        ideal_factor_a_max: 42,  // -0.5σ
        ideal_factor_b_min: 25,  // -1.5σ
        ideal_factor_b_max: 42,  // -0.5σ
        ideal_factor_c_min: 58,  // +0.5σ
        ideal_factor_c_max: 75,  // +1.5σ
        ideal_factor_d_min: 58,  // +0.5σ
        ideal_factor_d_max: 75,  // +1.5σ
      }
    };

    setExtractedData(extracted);
    if (onExtract) {
      onExtract(extracted);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        PI Job Report Helper
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Step 1: Get the PI Job Report PDF</strong>
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          1. In the Predictive Index platform, go to <strong>Hire</strong> → <strong>Job Target</strong>
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          2. Select your job from the list
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          3. Look for <strong>"Job report"</strong> in the bottom left menu
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          4. Click the <strong>download button</strong> in the top right to get the PDF
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Step 2: Extract data from the PDF</strong>
        </Typography>
        <Typography variant="body2">
          1. Open the downloaded PDF and select all text (Ctrl+A) <br/>
          2. Copy the text (Ctrl+C) <br/>
          3. Paste it in the field below <br/>
          4. Click "Extract Data"
        </Typography>
      </Alert>

      <TextField
        fullWidth
        multiline
        rows={6}
        label="Paste PDF text here"
        value={pdfText}
        onChange={(e) => setPdfText(e.target.value)}
        placeholder="Paste the entire text content from your PI Job Report PDF..."
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        onClick={handleTextAnalysis}
        disabled={!pdfText.trim()}
        sx={{ mb: 2 }}
      >
        Extract Data
      </Button>

      {extractedData && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Extracted Information:
          </Typography>
          
          {extractedData.jobTitle && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Job Title:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {extractedData.jobTitle}
              </Typography>
            </Box>
          )}

          {extractedData.department && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Department:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {extractedData.department}
              </Typography>
            </Box>
          )}
          
          {extractedData.jobSummary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Job Summary:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {extractedData.jobSummary}
              </Typography>
            </Box>
          )}

          {extractedData.behaviors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Behavioral Traits:
              </Typography>
              <ul style={{ margin: 0 }}>
                {extractedData.behaviors.map((behavior: string, idx: number) => (
                  <li key={idx}>
                    <Typography variant="body2">{behavior}</Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}

          {extractedData.commonProfiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Common Reference Profiles:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {extractedData.commonProfiles.map((profile: string, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ fontWeight: 'bold' }}>
                    • {profile}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Note: The behavioral target graph values cannot be automatically extracted from the PDF text. 
              Please manually adjust the PI factor ranges based on the visual graph in your PDF. 
              The suggested ranges above are estimates based on the job description.
            </Typography>
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default PIJobReportHelper;